/**
 * User Profile Page
 *
 * Features:
 * - User bio and stats
 * - Challenge creation history
 * - Submission gallery
 * - Follower/following system
 * - Social proof indicators
 * - Achievements/badges
 * - Profile editing for own profile
 * - Share profile link
 */

import {
  Settings,
  Share2,
  MapPin,
  Link as LinkIcon,
  Calendar,
  Medal,
  Trophy,
  Zap,
  Users,
  MessageSquare,
  Heart,
  Copy,
  Check,
  Edit3,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { cn } from '@/lib/cn';

interface UserProfile {
  id: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  location?: string;
  website?: string;
  joinedAt: string;
  followers: number;
  following: number;
  challengesCreated: number;
  submissionsCount: number;
  jemsCastedCount: number;
  jemReceived: number;
  badges: Badge[];
  isFollowing: boolean;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: string;
}

interface Challenge {
  id: string;
  title: string;
  category: string;
  submissionCount: number;
  totalJems: number;
  createdAt: string;
}

interface Submission {
  id: string;
  challengeId: string;
  challengeTitle: string;
  mediaUrl: string;
  jemCount: number;
  createdAt: string;
}

// Mock user profile
const mockProfile: UserProfile = {
  id: 'user123',
  displayName: 'Alex Chen',
  avatarUrl: 'https://picsum.photos/200/200?random=1',
  bio: '📸 Photography enthusiast | 🎵 Music lover | Always looking for creative challenges',
  location: 'San Francisco, CA',
  website: 'alexchen.photo',
  joinedAt: '2024-01-15',
  followers: 1240,
  following: 342,
  challengesCreated: 12,
  submissionsCount: 48,
  jemsCastedCount: 127,
  jemReceived: 3420,
  isFollowing: false,
  badges: [
    {
      id: 'badge1',
      name: 'Creator',
      description: 'Created 10+ challenges',
      icon: '🎯',
      unlockedAt: '2024-03-10',
    },
    {
      id: 'badge2',
      name: 'Trendsetter',
      description: 'Challenge received 1000+ jems',
      icon: '🔥',
      unlockedAt: '2024-04-20',
    },
    {
      id: 'badge3',
      name: 'Community Star',
      description: '1000+ followers',
      icon: '⭐',
      unlockedAt: '2024-05-01',
    },
  ],
};

const mockChallenges: Challenge[] = [
  {
    id: 'ch1',
    title: 'Best Coffee Shop Photo',
    category: 'Photography',
    submissionCount: 87,
    totalJems: 1240,
    createdAt: '2024-05-01',
  },
  {
    id: 'ch2',
    title: 'Sunset Photography Challenge',
    category: 'Photography',
    submissionCount: 64,
    totalJems: 890,
    createdAt: '2024-04-15',
  },
  {
    id: 'ch3',
    title: 'Golden Hour Magic',
    category: 'Photography',
    submissionCount: 52,
    totalJems: 756,
    createdAt: '2024-03-20',
  },
];

const mockSubmissions: Submission[] = [
  {
    id: 's1',
    challengeId: 'ch1',
    challengeTitle: 'Best Coffee Shop Photo',
    mediaUrl: 'https://picsum.photos/200/200?random=10',
    jemCount: 12,
    createdAt: '2024-05-05',
  },
  {
    id: 's2',
    challengeId: 'ch2',
    challengeTitle: 'Sunset Photography Challenge',
    mediaUrl: 'https://picsum.photos/200/200?random=11',
    jemCount: 18,
    createdAt: '2024-04-20',
  },
  {
    id: 's3',
    challengeId: 'ch3',
    challengeTitle: 'Golden Hour Magic',
    mediaUrl: 'https://picsum.photos/200/200?random=12',
    jemCount: 15,
    createdAt: '2024-04-10',
  },
];

/**
 * User profile with stats, achievements, challenges, and submissions.
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();

  const [profile, setProfile] = useState<UserProfile>(mockProfile);
  const [isFollowing, setIsFollowing] = useState(profile.isFollowing);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copyLink, setCopyLink] = useState(false);
  const [tab, setTab] = useState<'challenges' | 'submissions' | 'about'>('challenges');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: profile.displayName,
    bio: profile.bio,
    location: profile.location || '',
    website: profile.website || '',
  });

  // TODO: Phase 2 - Check if viewing own profile
  const isOwnProfile = true;

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    // TODO: Phase 2 - POST /api/users/:userId/follow or /unfollow
  };

  const handleCopyLink = () => {
    const profileUrl = `${window.location.origin}/profile/${profile.id}`;
    navigator.clipboard.writeText(profileUrl);
    setCopyLink(true);
    setTimeout(() => setCopyLink(false), 2000);
  };

  const handleSaveProfile = async () => {
    // TODO: Phase 2 - PATCH /api/users/me
    console.log('[profile] Saving profile:', editForm);
    setProfile({ ...profile, ...editForm });
    setIsEditing(false);
  };

  const joinedDate = new Date(profile.joinedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });

  return (
    <>
      {/* Header with Avatar */}
      <div className="mb-6">
        {/* Avatar & Info */}
        <div className="px-0">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-6">
            {/* Avatar */}
            <img
              src={profile.avatarUrl}
              alt={profile.displayName}
              className="h-20 w-20 lg:h-24 lg:w-24 rounded-full border-4 border-white shadow-lg object-cover flex-shrink-0"
            />

            {/* Name & Actions */}
            <div className="flex-1 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">{profile.displayName}</h1>
                <p className="text-slate-600 text-sm mt-1">@{profile.displayName.toLowerCase().replace(/\s+/g, '')}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                {isOwnProfile ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-slate-300 bg-white text-slate-900 font-semibold hover:bg-slate-50 transition-all text-sm whitespace-nowrap"
                  >
                    <Edit3 className="h-4 w-4" />
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleFollow}
                      className={cn(
                        'px-4 lg:px-6 py-2 rounded-full font-semibold transition-all text-sm whitespace-nowrap',
                        isFollowing
                          ? 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                          : 'bg-brand-600 text-white hover:bg-brand-700',
                      )}
                    >
                      {isFollowing ? 'Following' : 'Follow'}
                    </button>
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center justify-center gap-1 px-3 py-2 rounded-full border border-slate-300 bg-white text-slate-900 font-semibold hover:bg-slate-50 transition-all relative text-sm"
                    >
                      <Share2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Share</span>
                      {showShareMenu && (
                        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 z-10 min-w-48">
                          <button
                            onClick={handleCopyLink}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-200"
                          >
                            {copyLink ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                            {copyLink ? 'Copied!' : 'Copy link'}
                          </button>
                          <button
                            onClick={() => window.open(`https://twitter.com/intent/tweet?url=${window.location.href}`, '_blank')}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                          >
                            𝕏 Share on X
                          </button>
                        </div>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      {(profile.bio || profile.location || profile.website) && (
        <div className="mb-6 bg-white rounded-xl p-4 border border-slate-200">
          {profile.bio && <p className="text-slate-700 text-sm leading-relaxed">{profile.bio}</p>}
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
            {profile.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {profile.location}
              </div>
            )}
            {profile.website && (
              <a
                href={`https://${profile.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-brand-600 hover:underline"
              >
                <LinkIcon className="h-4 w-4" />
                {profile.website}
              </a>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Joined {joinedDate}
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <p className="text-2xl font-bold text-brand-600">{profile.challengesCreated}</p>
          <p className="text-xs text-slate-600 mt-1">Challenges</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <p className="text-2xl font-bold text-slate-900">{profile.submissionsCount}</p>
          <p className="text-xs text-slate-600 mt-1">Submissions</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <p className="text-2xl font-bold text-yellow-500">{profile.jemReceived}</p>
          <p className="text-xs text-slate-600 mt-1">Jems Received</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <p className="text-2xl font-bold text-slate-900">{profile.followers}</p>
          <p className="text-xs text-slate-600 mt-1">Followers</p>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <p className="text-2xl font-bold text-slate-900">{profile.following}</p>
          <p className="text-xs text-slate-600 mt-1">Following</p>
        </div>
      </div>

      {/* Achievements/Badges */}
      {profile.badges.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-3">Achievements</h2>
          <div className="grid grid-cols-3 gap-3">
            {profile.badges.map((badge) => (
              <div
                key={badge.id}
                className="bg-white rounded-lg p-4 border border-slate-200 text-center hover:shadow-md transition-shadow"
              >
                <p className="text-3xl mb-2">{badge.icon}</p>
                <p className="font-semibold text-sm text-slate-900">{badge.name}</p>
                <p className="text-xs text-slate-600 mt-1">{badge.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-slate-200">
        <div className="flex gap-6">
          <button
            onClick={() => setTab('challenges')}
            className={cn(
              'pb-3 font-semibold text-sm border-b-2 transition-colors',
              tab === 'challenges'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-slate-600 hover:text-slate-900',
            )}
          >
            Challenges ({profile.challengesCreated})
          </button>
          <button
            onClick={() => setTab('submissions')}
            className={cn(
              'pb-3 font-semibold text-sm border-b-2 transition-colors',
              tab === 'submissions'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-slate-600 hover:text-slate-900',
            )}
          >
            Submissions ({profile.submissionsCount})
          </button>
          <button
            onClick={() => setTab('about')}
            className={cn(
              'pb-3 font-semibold text-sm border-b-2 transition-colors',
              tab === 'about'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-slate-600 hover:text-slate-900',
            )}
          >
            About
          </button>
        </div>
      </div>

      {/* Challenges Tab */}
      {tab === 'challenges' && (
        <div className="space-y-3">
          {mockChallenges.map((challenge) => (
            <button
              key={challenge.id}
              onClick={() => navigate(`/challenge/${challenge.id}`)}
              className="w-full bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-all text-left"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{challenge.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {challenge.submissionCount} submissions • {challenge.category}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-brand-600 flex items-center gap-1">
                    <Zap className="h-4 w-4" />
                    {challenge.totalJems}
                  </p>
                  <p className="text-xs text-slate-600">jems</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Submissions Tab */}
      {tab === 'submissions' && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          {mockSubmissions.map((submission) => (
            <button
              key={submission.id}
              onClick={() => navigate(`/challenge/${submission.challengeId}`)}
              className="group relative rounded-lg overflow-hidden aspect-square bg-slate-200 hover:opacity-90 transition-opacity"
            >
              <img
                src={submission.mediaUrl}
                alt={submission.challengeTitle}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="text-center text-white">
                  <p className="font-semibold text-sm">{submission.challengeTitle}</p>
                  <p className="text-xs mt-1 flex items-center justify-center gap-1">
                    <Zap className="h-3 w-3" />
                    {submission.jemCount}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* About Tab */}
      {tab === 'about' && (
        <div className="space-y-4 bg-white rounded-lg p-6 border border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Stats</h3>
            <div className="space-y-2 text-sm text-slate-700">
              <p>
                <span className="font-semibold">{profile.jemsCastedCount}</span> jems cast (votes)
              </p>
              <p>
                <span className="font-semibold">{profile.jemReceived}</span> jems received on challenges
              </p>
              <p>
                <span className="font-semibold">{profile.followers}</span> followers
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-semibold text-slate-900 mb-2">Badges</h3>
            <p className="text-sm text-slate-700">
              {profile.badges.length} achievement{profile.badges.length !== 1 ? 's' : ''} unlocked
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-semibold text-slate-900 mb-2">Member Since</h3>
            <p className="text-sm text-slate-700">{joinedDate}</p>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">Edit Profile</h2>
              <button
                onClick={() => setIsEditing(false)}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveProfile();
              }}
              className="space-y-4"
            >
              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">Display Name</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  maxLength={40}
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  maxLength={160}
                  rows={3}
                  placeholder="Tell us about yourself"
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 resize-none"
                />
                <p className="text-xs text-slate-500 mt-1">{editForm.bio.length}/160</p>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">Location</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  maxLength={40}
                  placeholder="City, Country"
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">Website</label>
                <input
                  type="text"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  maxLength={100}
                  placeholder="example.com (without https://)"
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 rounded-lg border border-slate-200 px-4 py-2 font-semibold text-slate-900 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 px-4 py-2 font-semibold text-white hover:shadow-brand-md transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Spacer for mobile bottom nav */}
      <div className="h-8" />
    </>
  );
}
